import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { TicketControl, useTicketLinks } from "../components/TicketLink";
import { AlertTriangle, Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PortalLink } from "../components/PortalLink";
import { LoadingBlock } from "../components/Spinner";
import { SummaryTiles } from "../components/RegisterToolbar";
import { DimensionHeading, StatusTag, type StatusTone } from "../components/Drawing";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import {
  ModalOverlay, ModalHeader, Field, input, textarea, Check, EmptyState, secondaryBtn, dangerBtn,
  fmtDateTime, relativeDue, tableHeadRow, type AttentionItem,
} from "./resilience/shared";

// Policy-governed remediation: the organisation's own playbooks and the runs they produce.
// Rules in backend/src/lib/remediation.ts; routes in routes/remediation.routes.ts. Every write
// needs org:admin. Contract in docs/REMEDIATION-AUTOMATION.md.

type Severity = "critical" | "high" | "medium" | "low";
type RunStatus = "proposed" | "approved" | "running" | "completed" | "partially_completed" | "failed" | "rejected" | "cancelled";
type StepStatus = "pending" | "done" | "skipped" | "failed";

interface ActionParams { priority?: Severity; dueInDays?: number; assignmentId?: string }
interface ActionSpec { type: string; params?: ActionParams }

interface Step {
  type: string;
  label: string;
  approvalRequired: boolean;
  params: ActionParams | undefined;
  status: StepStatus;
  result?: string;
  error?: string;
  at?: string;
}

interface Run {
  id: string;
  playbookId: string | null;
  trigger: string;
  subjectKind: string;
  subjectId: string;
  subjectLabel: string;
  status: RunStatus;
  plan: Step[];
  context: { refs?: Record<string, string | null> } | null;
  aiSummary: string | null;
  decidedByUserId: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  playbook: { name: string; mode: string } | null;
}

interface Conditions { minSeverity?: Severity; minAssetCriticality?: Severity; essentialOnly?: boolean; minOverdueDays?: number }

interface Playbook {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  trigger: string;
  conditions: Conditions | null;
  actions: ActionSpec[];
  mode: "propose" | "auto";
  aiAssist: boolean;
  lastTriggeredAt: string | null;
  _count?: { runs: number };
}

interface TriggerDef { id: string; label: string; subject: string }
interface ActionDef { id: string; label: string; approvalRequired: boolean; subjects: readonly string[] }

interface RemediationResponse {
  playbooks: Playbook[];
  runs: Run[];
  attention: AttentionItem[];
  catalogue: { triggers: TriggerDef[]; actions: ActionDef[]; modes: string[] };
  trainingAssignments: Array<{ id: string; title: string }>;
  connections: { defender: boolean; intune: boolean };
  aiAvailable: boolean;
}

const RUN_STATUS: Record<RunStatus, { tone: StatusTone; label: string }> = {
  proposed:            { tone: "none",    label: "Waiting for approval" },
  approved:            { tone: "none",    label: "Approved" },
  running:             { tone: "partial", label: "Running" },
  completed:           { tone: "met",     label: "Completed" },
  partially_completed: { tone: "partial", label: "Partly completed" },
  failed:              { tone: "gap",     label: "Failed" },
  rejected:            { tone: "none",    label: "Rejected" },
  cancelled:           { tone: "none",    label: "Cancelled" },
};

const STEP_COLOR: Record<StepStatus, string> = { pending: theme.color.muted, done: theme.color.successText, skipped: theme.color.faint, failed: theme.color.dangerText };
const STEP_LABEL: Record<StepStatus, string> = { pending: "Pending", done: "Done", skipped: "Skipped", failed: "Failed" };

const STATUS_FILTERS: Array<{ id: string; label: string }> = [
  { id: "proposed", label: "Waiting for approval" },
  { id: "approved", label: "Approved" },
  { id: "running", label: "Running" },
  { id: "completed", label: "Completed" },
  { id: "failed", label: "Did not complete" },
  { id: "rejected", label: "Rejected" },
  { id: "cancelled", label: "Cancelled" },
];

const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];
const DAY = 86_400_000;

const pill = (bg: string, color: string): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: bg, color, whiteSpace: "nowrap" });
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", verticalAlign: "top" };
// The register table header every resilience and improvement register uses.
const headRow = tableHeadRow;
const sectionTitle: React.CSSProperties = { ...text.sectionTitle, margin: 0 };
const SEV_DOT: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

function RunPill({ status }: { status: RunStatus }) {
  const s = RUN_STATUS[status] ?? { tone: "none", label: status };
  return <StatusTag tone={s.tone}>{s.label}</StatusTag>;
}

const matchesStatus = (r: Run, filter: string) => !filter || (filter === "failed" ? r.status === "failed" || r.status === "partially_completed" : r.status === filter);

function stepsSummary(plan: Step[]): string {
  const done = plan.filter((s) => s.status === "done").length;
  const failed = plan.filter((s) => s.status === "failed").length;
  return `${done} of ${plan.length} done${failed ? `, ${failed} failed` : ""}`;
}

const needsDefender = (id: string) => id.startsWith("defender_");
const needsIntune = (id: string) => id === "intune_sync";

/** Who approves what a playbook proposes: org:admin or remediation:run (the Security Operations role). */
const APPROVERS = "an administrator or Security Operations";

// ── Page ──────────────────────────────────────────────────────────────────────

export function RemediationPage() {
  const { user } = useCurrentUser();
  // Writing a playbook is standing authority to act (org:admin); running, approving, retrying and
  // cancelling what the playbooks propose is remediation:run — the Security Operations role.
  const canAdmin = !!user?.permissions?.includes("org:admin");
  const canRun = !!user?.permissions?.includes("remediation:run");
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<RemediationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Playbook | "new" | null>(null);
  const [checking, setChecking] = useState<"idle" | "busy" | "queued">("idle");

  const statusFilter = params.get("status") ?? "";
  const setParam = (key: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(key, v); else next.delete(key);
    setParams(next, { replace: true });
  };
  const setStatusFilter = (v: string) => setParam("status", v);
  // ?run=<id> opens a run's read-only view; ?playbook=<id> a playbook's.
  const runParam = params.get("run");
  const playbookParam = params.get("playbook");

  const load = useCallback(() => {
    api.get<RemediationResponse>("/remediation")
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load remediation")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const history = useMemo(() => (data ? data.runs.filter((r) => matchesStatus(r, statusFilter)) : []), [data, statusFilter]);

  const act = async (path: string, fallback: string, body: Record<string, unknown> = {}) => {
    setActionError(null);
    try { await api.post(path, body); load(); }
    catch (e) { setActionError(apiErrorMessage(e, fallback)); }
  };

  const checkNow = async () => {
    setChecking("busy"); setActionError(null);
    try { await api.post("/remediation/evaluate", {}); setChecking("queued"); setTimeout(load, 15_000); }
    catch (e) { setActionError(apiErrorMessage(e, "Could not start the check")); setChecking("idle"); }
  };

  const toggleEnabled = async (p: Playbook) => {
    setActionError(null);
    try {
      await api.put(`/remediation/playbooks/${p.id}`, {
        name: p.name, description: p.description, enabled: !p.enabled, trigger: p.trigger, conditions: p.conditions ?? {},
        actions: p.actions, mode: p.mode, aiAssist: p.aiAssist,
      });
      load();
    } catch (e) { setActionError(apiErrorMessage(e, "Could not change the playbook")); }
  };

  const heading = (
    <>
      <h1 style={pageHeading}>Remediation</h1>
      <p style={{ ...pageSubtitle, maxWidth: 640 }}>
        Your own rules for what happens when something needs fixing. The platform raises actions, tells people and sends tickets straight away; anything that touches a device waits for a person to approve.
      </p>
    </>
  );

  if (error && !data) return <div className="page-pad">{heading}<div style={{ marginTop: 16, ...text.body, color: theme.color.dangerText }}>{error}</div></div>;
  if (!data) return <div className="page-pad">{heading}<LoadingBlock /></div>;

  const now = Date.now();
  const proposed = data.runs.filter((r) => r.status === "proposed");
  const recent = data.runs.filter((r) => now - new Date(r.createdAt).getTime() < 30 * DAY);
  const incomplete = recent.filter((r) => r.status === "failed" || r.status === "partially_completed").length;
  const triggerLabel = (id: string) => data.catalogue.triggers.find((t) => t.id === id)?.label ?? id;
  const actionLabel = (id: string) => data.catalogue.actions.find((a) => a.id === id)?.label ?? id;

  return (
    <div className="page-pad">
      {/* The same header as its sibling tab, the Improvement register: title left, actions right. */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>{heading}</div>
        {canRun && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={checkNow} disabled={checking === "busy"} style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={13} /> Check now
            </button>
            {canAdmin && (
              <button type="button" onClick={() => setEditing("new")} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
                <Plus size={14} /> New playbook
              </button>
            )}
          </div>
        )}
      </div>
      {checking === "queued" && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}`, ...text.body, color: theme.color.subtle }}>Checking — new plans appear here shortly.</div>}
      {actionError && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.danger}`, ...text.body, color: theme.color.danger }}>{actionError}</div>}

      <SummaryTiles tiles={[
        { key: "enabled", label: "Playbooks enabled", value: data.playbooks.filter((p) => p.enabled).length, hint: `of ${data.playbooks.length}` },
        { key: "recent", label: "Runs, last 30 days", value: recent.length },
        { key: "proposed", label: "Waiting for approval", value: proposed.length, tone: proposed.length > 0 ? "warning" : "neutral", onClick: () => setStatusFilter(statusFilter === "proposed" ? "" : "proposed"), active: statusFilter === "proposed" },
        { key: "failed", label: "Did not complete", value: incomplete, hint: "Last 30 days", tone: incomplete > 0 ? "danger" : "neutral", onClick: () => setStatusFilter(statusFilter === "failed" ? "" : "failed"), active: statusFilter === "failed" },
      ]} />

      <div style={{ display: "grid", gap: 20 }}>

        {data.attention.length > 0 && (
          <div style={{ ...card, padding: 0 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...sectionTitle }}>
              <AlertTriangle size={15} /> Needs attention
            </div>
            {data.attention.map((a) => (
              <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_DOT[a.severity], flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={text.itemTitle}>{a.title}</div>
                    <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                  </div>
                  {a.dueAt && <span style={{ ...text.meta, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                </div>
              </PortalLink>
            ))}
          </div>
        )}

        {proposed.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            <DimensionHeading as="h3" count={proposed.length} style={{ margin: 0 }}>Waiting for approval</DimensionHeading>
            {proposed.map((r) => <ProposalCard key={r.id} run={r} canRun={canRun} onOpen={() => setParam("run", r.id)} />)}
          </div>
        )}

        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <div style={sectionTitle}>Run history</div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Status">
              <option value="">All statuses</option>
              {STATUS_FILTERS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            {data.runs.length === 0 ? (
              <EmptyState title="No runs yet" detail="When a playbook's trigger fires, the plan it makes appears here: proposed for approval, or carried out straight away in automatic mode." />
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={headRow}>{["When", "Playbook", "Subject", "Status", "Decided by", "Steps"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {history.map((r) => <RunRow key={r.id} run={r} selected={r.id === runParam} onOpen={() => setParam("run", r.id)} />)}
                  {history.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: theme.color.muted }}>No runs with this status.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ display: "grid", gap: 10, minWidth: 0, flex: "2 1 420px" }}>
            <div style={sectionTitle}>Playbooks</div>
            {data.playbooks.length === 0 ? (
              <div style={card}>
                <EmptyState title="No playbooks yet"
                  detail="A playbook says: when this happens, for issues like this, do these things. Start with one that raises a corrective action and emails the owner when a known-exploited vulnerability is found."
                  action={canAdmin ? <button type="button" onClick={() => setEditing("new")} style={btnPrimary}>New playbook</button> : undefined} />
              </div>
            ) : data.playbooks.map((p) => (
              <div key={p.id} onClick={() => setParam("playbook", p.id)} role="button" style={{ ...card, padding: 16, display: "grid", gap: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={text.itemTitle}>{p.name}</div>
                    <div style={{ ...text.hint, marginTop: 2 }}>When: {triggerLabel(p.trigger)}</div>
                  </div>
                  <span style={pill(p.enabled ? theme.color.successBg : theme.color.panelAlt, p.enabled ? theme.color.successText : theme.color.muted)}>{p.enabled ? "Enabled" : "Off"}</span>
                </div>
                {p.description && <div style={{ ...text.hint, color: theme.color.text }}>{p.description}</div>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {p.actions.map((a) => <span key={a.type} style={pill(theme.color.accentBg, theme.color.accentText)}>{actionLabel(a.type)}</span>)}
                </div>
                <div style={text.hint}>
                  {p.mode === "auto" ? "Acts automatically" : "Proposes — waits for approval"}
                  {" · "}{p._count?.runs ?? 0} run{(p._count?.runs ?? 0) === 1 ? "" : "s"}
                  {p.lastTriggeredAt ? ` · last fired ${fmtDateTime(p.lastTriggeredAt)}` : ""}
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...card, padding: 18, flex: "1 1 260px" }}>
            <div style={{ ...sectionTitle, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}><ShieldCheck size={15} /> How it stays safe</div>
            <ul style={{ margin: 0, paddingLeft: 18, ...text.hint, color: theme.color.text, lineHeight: 1.6 }}>
              <li>Only the listed actions exist. There is no way to run anything else.</li>
              <li>Anything that touches a device always needs a named person to approve it. Isolating a device also needs its name typed in.</li>
              <li>One run per issue: a trigger that keeps firing for the same finding adds nothing until that run is finished.</li>
              <li>Every step is logged in the activity log and filed in the evidence repository.</li>
              <li>The AI can explain a plan. It cannot add to it or change it.</li>
            </ul>
          </div>
        </div>
      </div>

      {runParam && (() => {
        const r = data.runs.find((x) => x.id === runParam);
        return r ? (
          <RunView run={r} canRun={canRun} onClose={() => setParam("run", "")} onChanged={load}
            onRetry={() => {
              // Re-arming a failed isolation is approving it again: the server asks for the device name, as on approval.
              if (r.plan.some((s) => s.status === "failed" && s.type === "defender_isolate")) {
                const device = r.context?.refs?.assetName ?? "";
                const typed = window.prompt(`Retrying isolates ${device || "the device"} again. Type the device name to confirm.`);
                if (typed === null) return;
                void act(`/remediation/runs/${r.id}/retry`, "Could not retry the run", { confirmDevice: typed.trim() });
                return;
              }
              void act(`/remediation/runs/${r.id}/retry`, "Could not retry the run");
            }}
            onCancel={() => { if (window.confirm("Cancel this run? Nothing further will be done for it.")) void act(`/remediation/runs/${r.id}/cancel`, "Could not cancel the run"); }} />
        ) : null;
      })()}
      {playbookParam && !editing && (() => {
        const p = data.playbooks.find((x) => x.id === playbookParam);
        return p ? (
          <PlaybookView playbook={p} canAdmin={canAdmin} triggerLabel={triggerLabel} actionLabel={actionLabel}
            runs={data.runs.filter((r) => r.playbookId === p.id)}
            onOpenRun={(id) => { const next = new URLSearchParams(params); next.delete("playbook"); next.set("run", id); setParams(next, { replace: true }); }}
            onClose={() => setParam("playbook", "")} onEdit={() => setEditing(p)} onToggle={() => toggleEnabled(p)} />
        ) : null;
      })()}
      {editing && (
        <PlaybookModal
          playbook={editing === "new" ? null : editing}
          data={data}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function ProposalCard({ run, canRun, onOpen }: { run: Run; canRun: boolean; onOpen: () => void }) {
  return (
    <div onClick={onOpen} role="button" style={{ ...card, padding: 16, display: "grid", gap: 8, borderColor: theme.color.warning, cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}>{run.subjectLabel}</div>
          <div style={{ ...text.hint, marginTop: 2 }}>{run.playbook?.name ?? "Deleted playbook"} · proposed {fmtDateTime(run.createdAt)}</div>
        </div>
        <RunPill status={run.status} />
      </div>
      <div style={text.hint}>
        {run.plan.length} step{run.plan.length === 1 ? "" : "s"}
        {run.plan.some((st) => st.approvalRequired) ? " · includes a device action" : ""}
        {" · "}{canRun ? "open to approve or reject" : `${APPROVERS} approves or rejects this`}
      </div>
    </div>
  );
}

function RunRow({ run, selected, onOpen }: { run: Run; selected: boolean; onOpen: () => void }) {
  const decidedBy = run.decidedByName ?? (run.decidedAt ? "a former member" : run.status === "proposed" ? "—" : "automatic");
  return (
    <tr onClick={onOpen} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer", background: selected ? "var(--c-panelAlt)" : undefined }}>
      <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDateTime(run.createdAt)}</td>
      <td style={{ ...td, color: theme.color.text }}>{run.playbook?.name ?? "Deleted playbook"}</td>
      <td style={{ ...td, color: theme.color.text, minWidth: 200 }}>{run.subjectLabel}</td>
      <td style={td}><RunPill status={run.status} /></td>
      <td style={{ ...td, color: theme.color.muted }}>{decidedBy}</td>
      <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{stepsSummary(run.plan)}</td>
    </tr>
  );
}

/** The approve / reject decision on a proposed run, carried in the run view's Actions. */
function DecisionActions({ run, onDone }: { run: Run; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [device, setDevice] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isolates = run.plan.some((s) => s.type === "defender_isolate");
  const deviceName = run.context?.refs?.assetName ?? "";
  // The server refuses an isolation it cannot match to a named device, so Approve is not offered.
  const unnamedDevice = isolates && !deviceName.trim();

  const decide = async (decision: "approve" | "reject") => {
    if (decision === "reject" && !note.trim()) { setErr("Say why you are rejecting it"); return; }
    if (decision === "approve" && isolates && device.trim().toLowerCase() !== deviceName.trim().toLowerCase()) { setErr(`Type the device name (${deviceName || "unknown"}) to confirm isolating it`); return; }
    setBusy(true); setErr(null);
    try {
      await api.post(`/remediation/runs/${run.id}/decide`, { decision, note: note.trim() || null, ...(isolates && decision === "approve" ? { confirmDevice: device.trim() } : {}) });
      setBusy(false); setRejecting(false); setNote(""); setDevice("");
      onDone();
    } catch (e) { setErr(apiErrorMessage(e, "Could not record the decision")); setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gap: 8, width: "100%" }}>
      {unnamedDevice && (
        <div style={{ ...text.body, color: theme.color.warningText }}>
          This plan does not name the device to isolate, so it cannot be approved here. Reject it and isolate the device in Defender if it still needs it.
        </div>
      )}
      {isolates && !unnamedDevice && (
        <Field label={`Type the device name to confirm isolating it: ${deviceName}`} help="Isolation takes the device off the network until someone releases it in Defender.">
          <input value={device} onChange={(e) => setDevice(e.target.value)} style={{ ...input, maxWidth: 320 }} />
        </Field>
      )}
      {rejecting && (
        <Field label="Why are you rejecting it?">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </Field>
      )}
      {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {!rejecting ? (
          <>
            <button type="button" disabled={busy || unnamedDevice || (isolates && !device.trim())} title={unnamedDevice ? "The plan does not name the device to isolate" : undefined} onClick={() => decide("approve")} style={{ ...btnPrimary, opacity: busy || unnamedDevice || (isolates && !device.trim()) ? 0.6 : 1 }}>{busy ? "Saving…" : "Approve and run"}</button>
            <button type="button" disabled={busy} onClick={() => { setRejecting(true); setErr(null); }} style={dangerBtn}>Reject</button>
          </>
        ) : (
          <>
            <button type="button" disabled={busy} onClick={() => decide("reject")} style={dangerBtn}>{busy ? "Saving…" : "Reject"}</button>
            <button type="button" disabled={busy} onClick={() => { setRejecting(false); setErr(null); }} style={secondaryBtn}>Back</button>
          </>
        )}
      </div>
    </div>
  );
}

function RunView({ run, canRun, onClose, onChanged, onRetry, onCancel }: {
  run: Run; canRun: boolean; onClose: () => void; onChanged: () => void; onRetry: () => void; onCancel: () => void;
}) {
  const canRetry = canRun && (run.status === "failed" || run.status === "partially_completed");
  const canCancel = canRun && (run.status === "proposed" || run.status === "approved");
  const canDecide = canRun && run.status === "proposed";
  const decidedBy = run.decidedByName ?? (run.decidedAt ? "a former member" : run.status === "proposed" ? null : "Automatic");
  // A step can be raised as a ticket in the organisation's ticketing system while it is still to do.
  const stepRef = (i: number) => `${run.id}:${i}`;
  const tickets = useTicketLinks("remediation_step", run.plan.map((_, i) => stepRef(i)));
  const runLive = !["rejected", "cancelled", "completed"].includes(run.status);
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={run.subjectLabel} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        <DetailSection title="Run" aside={<RunPill status={run.status} />}>
          <DetailFields fields={[
            { label: "Playbook", value: run.playbook?.name ?? "Deleted playbook" },
            { label: "Mode", value: run.playbook ? (run.playbook.mode === "auto" ? "Acts automatically" : "Proposes, waits for approval") : null },
            { label: "Subject", value: run.subjectLabel },
            { label: "Steps", value: stepsSummary(run.plan) },
          ]} />
        </DetailSection>
        <DetailSection title="Plan">
          <div style={{ display: "grid", gap: 10 }}>
            {run.plan.map((st, i) => (
              <div key={i} style={{ display: "grid", gap: 2, fontSize: text.body.fontSize }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: theme.color.text }}>{i + 1}. {st.label}</span>
                  <span style={{ fontSize: text.meta.fontSize, fontWeight: 600, color: STEP_COLOR[st.status] }}>{STEP_LABEL[st.status] ?? st.status}</span>
                  {st.approvalRequired && <span style={pill(theme.color.warningBg, theme.color.warningText)}>needs approval</span>}
                  {st.at && <span style={text.meta}>{fmtDateTime(st.at)}</span>}
                </div>
                {(tickets.linkFor(stepRef(i)) || (runLive && (st.status === "pending" || st.status === "failed"))) && (
                  <div style={{ marginTop: 2 }}><TicketControl kind="remediation_step" subjectRef={stepRef(i)} links={tickets} canRaise={canRun} compact /></div>
                )}
                {st.result && <div style={{ ...text.hint }}>{st.result}</div>}
                {st.error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{st.error}</div>}
              </div>
            ))}
          </div>
        </DetailSection>
        {run.aiSummary && (
          <DetailSection title="Plan summary">
            <div style={{ ...text.hint, marginBottom: 6 }}>Written from the issue and the playbook's steps. It cannot change the plan.</div>
            <div style={{ ...text.body, whiteSpace: "pre-wrap" }}>{run.aiSummary}</div>
          </DetailSection>
        )}
        <DetailSection title="Timeline">
          <DetailFields fields={[
            { label: "Proposed", value: fmtDateTime(run.createdAt) },
            { label: "Decided by", value: decidedBy },
            { label: "Decided", value: run.decidedAt ? fmtDateTime(run.decidedAt) : null },
            { label: "Started", value: run.startedAt ? fmtDateTime(run.startedAt) : null },
            { label: "Finished", value: run.completedAt ? fmtDateTime(run.completedAt) : null },
            { label: "Decision note", value: run.decisionNote, wide: true, multiline: true },
          ]} />
        </DetailSection>
        {!canRun && run.status === "proposed" && <div style={{ ...text.hint, marginTop: 8 }}>{APPROVERS.charAt(0).toUpperCase() + APPROVERS.slice(1)} approves or rejects this.</div>}
        {(canDecide || canRetry || canCancel) && (
          <RecordActions note={canDecide ? "Approving runs the plan now. Rejecting needs a reason." : undefined}>
            {canDecide && <DecisionActions run={run} onDone={onChanged} />}
            {canRetry && <button type="button" onClick={onRetry} style={secondaryBtn}>Retry failed steps</button>}
            {canCancel && <button type="button" onClick={onCancel} style={dangerBtn}>Cancel run</button>}
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

function PlaybookView({ playbook: p, canAdmin, triggerLabel, actionLabel, runs, onOpenRun, onClose, onEdit, onToggle }: {
  playbook: Playbook; canAdmin: boolean; triggerLabel: (id: string) => string; actionLabel: (id: string) => string;
  runs: Run[]; onOpenRun: (id: string) => void; onClose: () => void; onEdit: () => void; onToggle: () => void;
}) {
  const c = p.conditions ?? {};
  const conditions = [
    c.minSeverity ? `${c.minSeverity[0].toUpperCase()}${c.minSeverity.slice(1)} severity or above` : null,
    c.minAssetCriticality ? `assets of ${c.minAssetCriticality} criticality or above` : null,
    c.essentialOnly ? "only assets that support an essential function" : null,
    c.minOverdueDays != null ? `overdue at least ${c.minOverdueDays} day${c.minOverdueDays === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join("; ");
  const actionDetail = (a: ActionSpec) => {
    const bits = [a.params?.priority ? `${a.params.priority} priority` : null, a.params?.dueInDays ? `due in ${a.params.dueInDays} days` : null].filter(Boolean);
    return `${actionLabel(a.type)}${bits.length ? ` (${bits.join(", ")})` : ""}`;
  };
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={p.name} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        {canAdmin && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}><EditButton onClick={onEdit} /></div>}
        <DetailSection title="Rule" aside={<span style={pill(p.enabled ? theme.color.successBg : theme.color.panelAlt, p.enabled ? theme.color.successText : theme.color.muted)}>{p.enabled ? "Enabled" : "Off"}</span>}>
          <DetailFields fields={[
            { label: "When", value: triggerLabel(p.trigger) },
            { label: "Only for", value: conditions || "Every issue this trigger raises" },
            { label: "How", value: p.mode === "auto" ? "Acts automatically" : "Proposes, waits for approval" },
            { label: "Description", value: p.description, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Does these things">
          <ol style={{ margin: 0, paddingLeft: 20, ...text.body, display: "grid", gap: 4 }}>
            {p.actions.map((a) => <li key={a.type}>{actionDetail(a)}</li>)}
          </ol>
        </DetailSection>
        <DetailSection title="Runs">
          <div style={{ ...text.hint, marginBottom: 8 }}>
            {p._count?.runs ?? runs.length} run{(p._count?.runs ?? runs.length) === 1 ? "" : "s"}{p.lastTriggeredAt ? ` · last fired ${fmtDateTime(p.lastTriggeredAt)}` : ""}
          </div>
          {runs.slice(0, 8).map((r) => (
            <div key={r.id} onClick={() => onOpenRun(r.id)} role="button" style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid var(--c-border)", cursor: "pointer", fontSize: text.body.fontSize }}>
              <span style={{ color: theme.color.text, minWidth: 0 }}>{r.subjectLabel} <span style={text.meta}>{fmtDateTime(r.createdAt)}</span></span>
              <RunPill status={r.status} />
            </div>
          ))}
        </DetailSection>
        {canAdmin && (
          <RecordActions>
            <button type="button" onClick={onToggle} style={secondaryBtn}>{p.enabled ? "Turn off" : "Turn on"}</button>
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Playbook editor ───────────────────────────────────────────────────────────

const SEVERITY_SUBJECTS = ["vulnerability", "advisory_exposure", "asset"];
const OVERDUE_SUBJECTS = ["training_assignee", "acknowledgement", "lifecycle_event", "vulnerability"];

function PlaybookModal({ playbook, data, onClose, onSaved }: { playbook: Playbook | null; data: RemediationResponse; onClose: () => void; onSaved: () => void }) {
  const { triggers, actions: actionDefs } = data.catalogue;
  const [name, setName] = useState(playbook?.name ?? "");
  const [description, setDescription] = useState(playbook?.description ?? "");
  const [trigger, setTrigger] = useState(playbook?.trigger ?? triggers[0]?.id ?? "");
  const c = playbook?.conditions ?? {};
  const [minSeverity, setMinSeverity] = useState<string>(c.minSeverity ?? "");
  const [minCriticality, setMinCriticality] = useState<string>(c.minAssetCriticality ?? "");
  const [essentialOnly, setEssentialOnly] = useState(!!c.essentialOnly);
  const [minOverdueDays, setMinOverdueDays] = useState<string>(c.minOverdueDays != null ? String(c.minOverdueDays) : "");
  const [selected, setSelected] = useState<Record<string, ActionParams>>(() => Object.fromEntries((playbook?.actions ?? []).map((a) => [a.type, a.params ?? {}])));
  const [mode, setMode] = useState<"propose" | "auto">(playbook?.mode ?? "propose");
  const [enabled, setEnabled] = useState(playbook?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const subject = triggers.find((t) => t.id === trigger)?.subject ?? "";
  const available = actionDefs.filter((a) => a.subjects.includes(subject));
  const chosen = available.filter((a) => a.id in selected);
  const hasDeviceAction = chosen.some((a) => a.approvalRequired);
  const showSeverity = SEVERITY_SUBJECTS.includes(subject);
  const showOverdue = OVERDUE_SUBJECTS.includes(subject);

  const missingConnection = (id: string): string | null => {
    if (needsDefender(id) && !data.connections.defender) return "Connect Microsoft Defender first (Connections).";
    if (needsIntune(id) && !data.connections.intune) return "Connect Intune first (Connections).";
    return null;
  };

  const toggle = (a: ActionDef, on: boolean) => {
    const next = { ...selected };
    if (on) {
      next[a.id] = a.id === "create_corrective_action" ? { priority: "high", dueInDays: 14 } : a.id === "assign_training" ? { assignmentId: data.trainingAssignments[0]?.id } : {};
      if (a.approvalRequired) setMode("propose");
    } else delete next[a.id];
    setSelected(next);
  };
  const setParam = (id: string, p: ActionParams) => setSelected({ ...selected, [id]: { ...selected[id], ...p } });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Give the playbook a name"); return; }
    if (chosen.length === 0) { setErr("Add at least one action"); return; }
    const conditions: Conditions = {};
    if (showSeverity) {
      if (minSeverity) conditions.minSeverity = minSeverity as Severity;
      if (minCriticality) conditions.minAssetCriticality = minCriticality as Severity;
      if (essentialOnly) conditions.essentialOnly = true;
    }
    if (showOverdue && minOverdueDays !== "") {
      const n = Number(minOverdueDays);
      if (!Number.isInteger(n) || n < 0 || n > 365) { setErr("Overdue days must be a whole number between 0 and 365"); return; }
      conditions.minOverdueDays = n;
    }
    const actions: ActionSpec[] = chosen.map((a) => {
      const p = selected[a.id] ?? {};
      if (a.id === "create_corrective_action") return { type: a.id, params: { ...(p.priority ? { priority: p.priority } : {}), ...(p.dueInDays ? { dueInDays: p.dueInDays } : {}) } };
      if (a.id === "assign_training") return { type: a.id, params: p.assignmentId ? { assignmentId: p.assignmentId } : {} };
      return { type: a.id };
    });
    const body = { name: name.trim(), description: description.trim() || null, enabled, trigger, conditions, actions, mode: hasDeviceAction ? "propose" : mode };
    setSaving(true); setErr(null);
    try {
      if (playbook) await api.put(`/remediation/playbooks/${playbook.id}`, body);
      else await api.post("/remediation/playbooks", body);
      onSaved();
    } catch (e2) { setErr(apiErrorMessage(e2, "Could not save the playbook")); setSaving(false); }
  };

  const remove = async () => {
    if (!playbook || !window.confirm(`Delete "${playbook.name}"? Plans it proposed that are still waiting are withdrawn. Past runs stay in the history.`)) return;
    setSaving(true); setErr(null);
    try { await api.del(`/remediation/playbooks/${playbook.id}`); onSaved(); }
    catch (e) { setErr(apiErrorMessage(e, "Could not delete the playbook")); setSaving(false); }
  };

  const sevSelect = (value: string, set: (v: string) => void, anyLabel: string) => (
    <select value={value} onChange={(e) => set(e.target.value)} style={input}>
      <option value="">{anyLabel}</option>
      {SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)} or above</option>)}
    </select>
  );

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={playbook ? `Edit playbook: ${playbook.name}` : "New playbook"} onClose={onClose} />
      <form onSubmit={save} style={{ padding: 20, display: "grid", gap: 16 }}>
        {playbook && <div style={text.hint}>Saving changes withdraws any plans this playbook proposed that are still waiting for approval; it proposes them again under the new rule.</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} style={input} maxLength={200} /></Field>
          <Field label="When">
            <select value={trigger} onChange={(e) => setTrigger(e.target.value)} style={input}>
              {triggers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Description (optional)" span2><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} style={textarea} maxLength={2000} /></Field>
        </div>

        {(showSeverity || showOverdue) && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ ...text.label, marginBottom: 0 }}>Only for issues like this</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
              {showSeverity && <Field label="Minimum severity">{sevSelect(minSeverity, setMinSeverity, "Any severity")}</Field>}
              {showSeverity && <Field label="Minimum asset criticality">{sevSelect(minCriticality, setMinCriticality, "Any asset")}</Field>}
              {showOverdue && (
                <Field label="Overdue at least (days)" help="Leave empty to act as soon as it is overdue">
                  <input type="number" min={0} max={365} value={minOverdueDays} onChange={(e) => setMinOverdueDays(e.target.value)} style={input} />
                </Field>
              )}
            </div>
            {showSeverity && <Check checked={essentialOnly} onChange={setEssentialOnly} label="Only assets that support an essential function" />}
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ ...text.label, marginBottom: 0 }}>Do these things</div>
          {available.map((a) => {
            const on = a.id in selected;
            const missing = missingConnection(a.id);
            const disabled = !!missing && !on;
            return (
              <div key={a.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", display: "grid", gap: 8, opacity: disabled ? 0.6 : 1 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: text.body.fontSize, cursor: disabled ? "not-allowed" : "pointer", color: theme.color.text }}>
                  <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => toggle(a, e.target.checked)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1 }}>
                    {a.label}
                    {a.approvalRequired && <span style={{ ...pill(theme.color.warningBg, theme.color.warningText), marginLeft: 8 }}>needs approval</span>}
                    {missing && <span style={{ display: "block", ...text.meta }}>{missing}</span>}
                  </span>
                </label>
                {on && a.id === "create_corrective_action" && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, paddingLeft: 24 }}>
                    <Field label="Priority">
                      <select value={selected[a.id]?.priority ?? "high"} onChange={(e) => setParam(a.id, { priority: e.target.value as Severity })} style={input}>
                        {SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </Field>
                    <Field label="Due in (days)" help="A vulnerability's own deadline wins when it has one">
                      <input type="number" min={1} max={365} value={selected[a.id]?.dueInDays ?? ""} onChange={(e) => setParam(a.id, { dueInDays: e.target.value ? Number(e.target.value) : undefined })} style={input} />
                    </Field>
                  </div>
                )}
                {on && a.id === "assign_training" && (
                  <div style={{ paddingLeft: 24 }}>
                    <Field label="Course">
                      {data.trainingAssignments.length === 0 ? (
                        <span style={text.hint}>No active training assignments. Create one under Training first.</span>
                      ) : (
                        <select value={selected[a.id]?.assignmentId ?? ""} onChange={(e) => setParam(a.id, { assignmentId: e.target.value })} style={input}>
                          <option value="">Choose a course</option>
                          {data.trainingAssignments.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                        </select>
                      )}
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ ...text.label, marginBottom: 0 }}>How</div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, ...text.body, cursor: "pointer" }}>
            <input type="radio" name="mode" checked={mode === "propose" || hasDeviceAction} onChange={() => setMode("propose")} style={{ marginTop: 3 }} />
            <span>Propose — waits for {APPROVERS} to approve<span style={{ display: "block", ...text.meta }}>The plan appears under Waiting for approval; nothing happens until someone approves it.</span></span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, ...text.body, cursor: hasDeviceAction ? "not-allowed" : "pointer", opacity: hasDeviceAction ? 0.6 : 1 }}>
            <input type="radio" name="mode" checked={mode === "auto" && !hasDeviceAction} disabled={hasDeviceAction} onChange={() => setMode("auto")} style={{ marginTop: 3 }} />
            <span>Act automatically<span style={{ display: "block", ...text.meta }}>{hasDeviceAction ? "Not available: an action on a device always needs a person to approve it." : "The steps run as soon as the trigger fires."}</span></span>
          </label>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <Check checked={enabled} onChange={setEnabled} label="Enabled" />
        </div>

        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div>{playbook && <button type="button" onClick={remove} disabled={saving} style={dangerBtn}>Delete playbook</button>}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save playbook"}</button>
          </div>
        </div>
      </form>
    </ModalOverlay>
  );
}
